// packages/web/src/routes/_authed/work/day/$date.tsx
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { workerWeekOptions } from "@/api/me/query-options";
import { isoWeekFromDate } from "@/lib/temporal";
import { Button } from "@/components/ui/button";
import type {
  HoursEntry,
  LodgingDeclaration,
  LodgingInvoiceStatus,
} from "@/api/generated/types.gen";

export const Route = createFileRoute("/_authed/work/day/$date")({
  component: DayDetail,
});

function DayDetail() {
  const { t } = useTranslation();
  const { date } = Route.useParams() as { date: string };
  const navigate = useNavigate();
  const { user } = Route.useRouteContext() as { user: { id: string } };
  // Use noon UTC to avoid date-boundary edge cases when deriving the ISO week.
  const week = isoWeekFromDate(new Date(`${date}T12:00:00Z`));
  const q = useQuery(workerWeekOptions(user.id, week));

  // HoursEntry.date is YYYY-MM-DD in Europe/Amsterdam wall-clock time.
  const entriesForDay: HoursEntry[] = (q.data?.entries ?? []).filter(
    (e: HoursEntry) => e.date === date,
  );

  // LodgingDeclaration.nightEndedOn is epoch ms. The night "ends" on the morning of the
  // next day, so we match on that date (the date the worker wakes up at the lodging).
  const lodgingForDay: LodgingDeclaration | undefined = (q.data?.lodgings ?? []).find(
    (l: LodgingDeclaration) => new Date(l.nightEndedOn).toISOString().slice(0, 10) === date,
  );

  function lodgingStatusLabel(status: LodgingInvoiceStatus): string {
    return t(`work.day.lodgingStatus.${status}`);
  }

  return (
    <div className="px-4 py-4 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{date}</h1>
        <Button
          onClick={() =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            void navigate({ to: "/work/new/type" as any, search: { date } as any })
          }
        >
          {t("work.day.addEntry")}
        </Button>
      </header>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-ink-500">{t("work.day.entries")}</h2>
        {entriesForDay.length === 0 ? (
          <p className="text-ink-500">{t("work.day.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {entriesForDay.map((e: HoursEntry) => (
              <li key={e.id} className="rounded-4 border-line border bg-surface p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t(`work.type.${e.type}`)}</div>
                    <div className="text-xs text-ink-500">
                      {new Date(e.startAt).toLocaleTimeString("nl-NL", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      –{" "}
                      {new Date(e.endAt).toLocaleTimeString("nl-NL", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <span className="text-xs text-ink-500">{e.status}</span>
                </div>
                {e.note ? <p className="text-sm mt-1">{e.note}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {lodgingForDay ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-ink-500">{t("work.day.lodging")}</h2>
          <div className="rounded-4 border-line border bg-surface p-3">
            <div className="font-medium">{lodgingForDay.lodgingAddressId}</div>
            <div className="text-xs text-ink-500">
              {lodgingStatusLabel(lodgingForDay.invoiceStatus)}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
